import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { MtwProvider, SonnerToaster } from '@makyoapp/makyo-tw-ui'

import { ReactFrame, useFrameContext } from '@/lib/react-frame'
import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useFontLink } from '@/hooks/__project__/useFontLink'
import { EVENT_KEYS } from '@/shared/custom-event'

/**
 * Frame Content
 */
function FrameContent({ children }: { children?: React.ReactNode }) {
  const { document: frameDocument } = useFrameContext()
  const [colorScheme] = useBuilderColorSchemeV2()
  const { brandkitSourceColor, brandkitExtendedColors, brandkitTypography } = useAppBrandKitContext()

  if (!frameDocument) return null
  return (
    <MtwProvider
      sourceColor={brandkitSourceColor}
      extendedColors={brandkitExtendedColors}
      typography={brandkitTypography}
      ctxDocument={frameDocument}
      mode={colorScheme.current}
    >
      {children}
      <SonnerToaster visibleToasts={100} position='bottom-right' />
    </MtwProvider>
  )
}

/**
 * Frame Head
 */
function FrameHead() {
  const { customFonts, deviceScale } = useAppBuilderContext()
  const { activePage } = useAppPagesContext()
  const { brandkitTypography } = useAppBrandKitContext()
  const { fontLink, customFontLink } = useFontLink(brandkitTypography, customFonts)

  return (
    <>
      <meta charSet='utf-8' />
      <title>Previewer</title>
      <meta
        name='viewport'
        content={`width=device-width, initial-scale=${deviceScale}, maximum-scale=${deviceScale}`}
      />
      {fontLink && <link href={fontLink} type='text/css' rel='stylesheet' />}
      {customFontLink && <link href={customFontLink} type='text/css' rel='stylesheet' />}
      <base target='_parent' />

      <style>
        {`/* Skeleton shimmer for Material Symbols when font is not ready */
        :root {
          --icon-skeleton-base: rgba(0, 0, 0, 0.08);
          --icon-skeleton-highlight: rgba(0, 0, 0, 0.18);
        }
        @media (prefers-color-scheme: dark) {
          :root {
            --icon-skeleton-base: rgba(255, 255, 255, 0.12);
            --icon-skeleton-highlight: rgba(255, 255, 255, 0.24);
          }
        }

        /* Base & size guard while font not ready (restrict to actual icon classes) */
        :root:not(.icons-ready) .material-icon,
        :root:not(.icons-ready) .material-icons,
        :root:not(.icons-ready) .material-symbols-rounded,
        :root:not(.icons-ready) .material-symbols-outlined {
          color: transparent !important; /* hide glyph without removing box */
          position: relative;
          display: inline-block; /* ensure can be given width/height */
          width: 1em; /* limit width to avoid text ligature */
          height: 1em;
          line-height: 1em;
          overflow: hidden; /* hide text fallback (e.g. "home") */
          vertical-align: middle;
          white-space: nowrap;
        }

        :root:not(.icons-ready) .material-icon::after,
        :root:not(.icons-ready) .material-icons::after,
        :root:not(.icons-ready) .material-symbols-rounded::after,
        :root:not(.icons-ready) .material-symbols-outlined::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          width: 1em;
          height: 1em;
          transform: translate(-50%, -50%);
          border-radius: 0.25em;
          background: linear-gradient(
            90deg,
            var(--icon-skeleton-base) 20%,
            var(--icon-skeleton-highlight) 50%,
            var(--icon-skeleton-base) 80%
          );
          background-size: 200% 100%;
          animation: icon-skeleton-shimmer 1.3s ease-in-out infinite;
          will-change: background-position, transform;
        }

        .icons-ready .material-icon,
        .icons-ready .material-icons,
        .icons-ready .material-symbols-rounded,
        .icons-ready .material-symbols-outlined {
          color: inherit;
          width: auto;
          height: auto;
          overflow: visible;
          white-space: inherit;
          vertical-align: baseline;
        }

        .icons-ready .material-icon::after,
        .icons-ready .material-icons::after,
        .icons-ready .material-symbols-rounded::after,
        .icons-ready .material-symbols-outlined::after {
          content: none;
        }

        @keyframes icon-skeleton-shimmer {
          0% { background-position: -150% 0; }
          100% { background-position: 150% 0; }
        }`}
      </style>

      <script>{`
        document.fonts.load('24px "Material Symbols Rounded"')
          .then(() => {
            document.documentElement.classList.add('icons-ready')
          })
          .catch(() => {
            setTimeout(() => {
              document.documentElement.classList.add('icons-ready')
            }, 2000)
          })
      `}</script>

      <style>{`
        html {
          font-size: 16px;
        }
        body {
          text-rendering: optimizelegibility; 
          -webkit-font-smoothing: subpixel-antialiased;
          color: var(--color-on-${activePage?.bgColor ?? 'primary-95'});
        } 
      `}</style>
    </>
  )
}

/**
 * Collect styles & links from the main (parent) document
 */
function getParentStaticStyles() {
  // Collect styles
  const styleNodes = Array.from(document.head.querySelectorAll('style'))
    .filter((s) => {
      return (
        s.dataset.viteDevId?.includes('@material-symbols') ||
        s.dataset.viteDevId?.includes('/src/index.css') ||
        s.dataset.viteDevId?.includes('makyo-tw-ui.css') ||
        s.innerText.startsWith('/*\n! tailwindcss ') ||
        s.innerText.startsWith('[data-sonner-toaster]')
      )
    })
    .map((s) => s.outerHTML)
    .join('\n')

  // Collect links
  const linkNodes = Array.from(document.head.querySelectorAll('link[rel="stylesheet"]'))
    .filter((link) => {
      const l = link as HTMLLinkElement
      const href = l.href.split('/').slice(3).join('/')

      // handle dev
      if (import.meta.env.DEV) {
        return href.startsWith('assets') && href.endsWith('.css')
      }

      // only include the main bundled css
      return href.startsWith('assets') && href.includes('index-') && href.endsWith('.css')
    })
    .map((l) => l.outerHTML)
    .join('\n')

  return `${styleNodes}\n${linkNodes}`
}

/**
 * App Builder Preview Frame
 */
export function PreviewFrame({ children }: { children?: React.ReactNode }) {
  const { previewFrameRef } = useAppBuilderContext()
  const { activePage } = useAppPagesContext()

  const [isFrameReady, setIsFrameReady] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleFrameLoaded = useCallback((frameDocument: Document | null) => {
    if (!frameDocument) return
    frameDocument.documentElement.classList.remove('icons-ready')
    frameDocument.fonts
      .load('24px "Material Symbols Rounded"')
      .then(() => {
        frameDocument.documentElement.classList.add('icons-ready')
      })
      .catch(() => {
        setTimeout(() => {
          frameDocument.documentElement.classList.add('icons-ready')
        }, 2000)
      })
    setIsFrameReady(true)
    window.dispatchEvent(new CustomEvent(EVENT_KEYS.APP_PREVIEW_LOADED))
  }, [])

  const initialFrameHtml = useMemo(() => {
    return `
    <!DOCTYPE html>
      <html>
        <head>
        ${getParentStaticStyles()}
        </head>
        <body class="frame-body">
          <div id="frame-root" class="frame-root"></div>
        </body>
      </html>
      `
  }, [])

  useEffect(() => {
    let timeout: NodeJS.Timeout
    const handleLoadFrame = () => {
      setIsFrameReady(false)
      timeout = setTimeout(() => {
        setIsFrameReady(true)
      }, 500)
    }
    window.addEventListener(EVENT_KEYS.RELOAD_PREVIEW_FRAME, handleLoadFrame)
    return () => {
      window.removeEventListener(EVENT_KEYS.RELOAD_PREVIEW_FRAME, handleLoadFrame)
      clearTimeout(timeout)
    }
  }, [])

  return (
    <ReactFrame
      ref={previewFrameRef}
      initialContent={initialFrameHtml}
      head={<FrameHead />}
      onFrameLoaded={handleFrameLoaded}
      className='w-full h-full z-0 border-none select-none'
    >
      {isFrameReady ? (
        <div className='h-full' ref={containerRef}>
          {children ? <FrameContent>{children}</FrameContent> : <div></div>}
        </div>
      ) : (
        <div
          role='status'
          className={`h-screen bg-${activePage?.bgColor ?? 'primary-95'} flex items-center justify-center`}
        >
          <span className='loading spin !size-8' />
        </div>
      )}
    </ReactFrame>
  )
}
